/**
 * AdminSettlementPage - 원장용 정산 대시보드 페이지
 *
 * /centers/:centerId/settlements 경로로 접근하며, 월별 강사 정산 현황을 표시한다.
 * - 매핑된 강사 목록을 Studiomate에서 로드하여 표시
 * - 강사별 정산 처리 (settleInstructor CF 호출)
 * - 월 네비게이션 (이전/다음 달 이동, URL searchParam으로 상태 관리)
 * - 정산 상태 배지 (calculating/confirmed/paid)
 * - 정산 확정/지급 완료 처리
 * - 라인 아이템 상세: 수업일, 회원명(링크), 회원 유형, 금액
 * - 라인 아이템 수동 조정 (calculating 상태에서만 가능)
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { useCurrentUser } from "@/components/AuthProvider";
import { getFirebaseFunctions } from "@/config/firebase";
import {
  getAdminSettlement,
  type AdminSettlementDocument,
  type InstructorSettlement,
  type SettlementLineItem,
} from "@/services/firestore/admin-settlements";
import {
  settleInstructor as settleInstructorAction,
  cancelInstructorSettlement as cancelInstructorSettlementAction,
  previewInstructorSettlement as previewInstructorSettlementAction,
} from "@/services/admin-settlement-actions";
import { useStudiomateStaffs } from "@/hooks/useStudiomateIntegration";
import type { InstructorMappingState } from "@vlab-corporation/studiomate-api";
import { getInstructors } from "@/services/firestore/admin-instructors";
import type { Instructor } from "@vlab-corporation/vlab-pilates-service-interface/staff";
import { normalizeClassType } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import { trackEvent, EVENT_NAMES } from "@/utils/analytics-tracking";

// --- 월 네비게이션 유틸리티 ---

/** 현재 연월을 "YYYY-MM" 형식으로 반환한다. */
function getCurrentYearMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** 주어진 연월의 이전 달을 "YYYY-MM" 형식으로 반환한다. */
function getPrevMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  if (month === 1) return `${year - 1}-12`;
  return `${year}-${String(month - 1).padStart(2, "0")}`;
}

/** 주어진 연월의 다음 달을 "YYYY-MM" 형식으로 반환한다. */
function getNextMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number);
  if (month === 12) return `${year + 1}-01`;
  return `${year}-${String(month + 1).padStart(2, "0")}`;
}

/** 현재 날짜 기준 이전 달을 "YYYY-MM" 형식으로 반환한다. */
function getPreviousYearMonth(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed: 0=1월
  if (month === 0) return `${year - 1}-12`;
  return `${year}-${String(month).padStart(2, "0")}`;
}

// --- 정산 상태 배지 ---

/** 정산 상태에 따른 배지 텍스트와 스타일을 반환한다. */
function getStatusBadge(status: AdminSettlementDocument["status"]): {
  label: string;
  className: string;
} {
  switch (status) {
    case "calculating":
      return {
        label: "산출 중",
        className: "bg-yellow-100 text-yellow-800",
      };
    case "confirmed":
      return {
        label: "확정",
        className: "bg-blue-100 text-blue-800",
      };
    case "paid":
      return {
        label: "지급 완료",
        className: "bg-green-100 text-green-800",
      };
  }
}

// --- 금액 포맷 ---

/**
 * 금액을 한국 원화 형식으로 포맷한다.
 * 예: 300000 → "300,000원"
 */
function formatAmount(amount: number): string {
  return amount.toLocaleString("ko-KR") + "원";
}

/** 원천세율 3.3% (사업소득세 3% + 지방소득세 0.3%) */
const WITHHOLDING_TAX_RATE = 0.033;

/** 세후 금액을 계산한다. */
function calculateAfterTax(amount: number): number {
  return amount - Math.round(amount * WITHHOLDING_TAX_RATE);
}

// --- CSV 내보내기 ---

/**
 * 정산 데이터를 CSV 형식으로 변환하여 파일 다운로드를 시작한다.
 * Excel 호환을 위해 UTF-8 BOM(\uFEFF)을 파일 앞에 추가한다.
 *
 * CSV 형식:
 *   강사ID,완료수업수,정산금액
 *   instructor-1,20,300000
 */
function exportSettlementToCsv(
  settlement: AdminSettlementDocument,
  yearMonth: string,
  instructorNameMap: Map<string, Instructor>,
): void {
  const header = "강사,완료수업수,정산금액";
  const rows = settlement.instructorSettlements.map(
    (item) =>
      `${instructorNameMap.get(item.instructorId)?.name ?? item.instructorId},${item.completedClasses},${item.totalAmount}`,
  );
  const csvContent = [header, ...rows].join("\n");

  // UTF-8 BOM + CSV 내용으로 Blob 생성
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `정산_${yearMonth}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

// --- 라인 아이템 조정 폼 ---

/** 라인 아이템 조정 폼의 편집 대상을 나타내는 타입 */
interface LineItemEditTarget {
  instructorId: string;
  classId: string;
  currentAmount: number;
}

/** adjustSettlementLineItem CF 요청 데이터 */
interface AdjustLineItemRequest {
  centerId: number;
  yearMonth: string;
  instructorId: string;
  classId: string;
  adjustedAmount: number;
  reason: string;
}

/**
 * LineItemAdjustForm - 라인 아이템 수동 조정 인라인 폼 컴포넌트
 *
 * 특정 라인 아이템의 금액을 조정하는 폼을 렌더링한다.
 * "저장" 클릭 시 adjustSettlementLineItem Cloud Function을 호출한다.
 */
function LineItemAdjustForm({
  centerId,
  yearMonth,
  target,
  onSuccess,
  onCancel,
}: {
  centerId: number;
  yearMonth: string;
  target: LineItemEditTarget;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [adjustedAmount, setAdjustedAmount] = useState<string>(String(target.currentAmount));
  const [reason, setReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    const parsedAmount = Number(adjustedAmount);

    // 클라이언트 측 입력 검증
    if (!Number.isInteger(parsedAmount) || parsedAmount < 0) {
      setSaveError("조정 금액은 0 이상의 정수여야 합니다.");
      return;
    }
    if (reason.trim().length === 0) {
      setSaveError("조정 사유를 입력해 주세요.");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const callable = httpsCallable<AdjustLineItemRequest, { success: boolean }>(
        getFirebaseFunctions(),
        "adjustSettlementLineItem",
      );
      await callable({
        centerId,
        yearMonth,
        instructorId: target.instructorId,
        classId: target.classId,
        adjustedAmount: parsedAmount,
        reason: reason.trim(),
      });
      onSuccess();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "라인 아이템 조정에 실패했습니다.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md space-y-2">
      <p className="text-xs font-medium text-yellow-800">수업 ID: {target.classId} 금액 조정</p>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-600 w-16 shrink-0">조정 금액</label>
        <input
          type="number"
          min={0}
          step={1}
          value={adjustedAmount}
          onChange={(e) => setAdjustedAmount(e.target.value)}
          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="금액 (원)"
          aria-label="조정 금액"
        />
        <span className="text-xs text-gray-500">(현재: {formatAmount(target.currentAmount)})</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-600 w-16 shrink-0">조정 사유</label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="조정 사유를 입력하세요"
          aria-label="조정 사유"
        />
      </div>
      {saveError && <p className="text-xs text-red-600">{saveError}</p>}
      <div className="flex items-center gap-2">
        <button
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "저장 중..." : "저장"}
        </button>
        <button
          onClick={onCancel}
          disabled={isSaving}
          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          취소
        </button>
      </div>
    </div>
  );
}

// --- 정산 기간 경고 모달 ---

/**
 * SettlementWarningModal - 이전 달이 아닌 기간에 정산 시 경고 모달
 *
 * 일지가 아직 모두 작성되지 않았을 수 있고,
 * 정산 후에는 되돌리기 어렵다는 것을 알린다.
 */
function SettlementWarningModal({
  isOpen,
  yearMonth,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  yearMonth: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-amber-800 mb-3">정산 기간 확인</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
          <p className="text-sm text-amber-900 mb-2">
            선택한 정산 기간(<span className="font-semibold">{yearMonth}</span>
            )은 이전 달이 아닙니다.
          </p>
          <ul className="text-sm text-amber-800 list-disc list-inside space-y-1">
            <li>일지가 아직 모두 작성되지 않았을 수 있습니다.</li>
            <li>정산 후에는 되돌리기 어렵습니다.</li>
          </ul>
        </div>
        <p className="text-sm text-gray-600 mb-4">계속 진행하시겠습니까?</p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
          >
            계속 진행
          </button>
        </div>
      </div>
    </div>
  );
}

// --- 강사별 정산 행 ---

/**
 * InstructorSettlementRow - 강사별 정산 행 컴포넌트
 *
 * 강사의 정산 금액과 라인 아이템 목록을 표시하고,
 * calculating 상태에서는 각 라인 아이템에 "조정" 버튼을 제공한다.
 */
function InstructorNameCell({
  instructorId,
  name,
  centerId,
  seniorSince,
  isOwner,
}: {
  instructorId: string;
  name: string | undefined;
  centerId: number;
  seniorSince?: string;
  isOwner?: boolean;
}) {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <span className="flex items-center gap-1.5">
        {name ? (
          <Link
            to={`/centers/${centerId}/instructors/${instructorId}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {name}
          </Link>
        ) : (
          instructorId
        )}
        {isOwner && (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-100 text-blue-700">
            원장
          </span>
        )}
        {seniorSince != null && (
          <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-100 text-purple-700">
            시니어
          </span>
        )}
      </span>
    </td>
  );
}

function InstructorSettlementRow({
  item,
  centerId,
  yearMonth,
  isCalculating,
  onAdjustSuccess,
  onCancel,
  isCancelling,
  cancelReasonTarget,
  cancelReason,
  onCancelReasonTargetChange,
  onCancelReasonChange,
  instructorInfo,
  isOwner,
}: {
  item: InstructorSettlement;
  centerId: number;
  yearMonth: string;
  isCalculating: boolean;
  onAdjustSuccess: () => void;
  onCancel?: (instructorId: string, reason: string) => void;
  isCancelling?: boolean;
  cancelReasonTarget?: string | null;
  cancelReason?: string;
  onCancelReasonTargetChange?: (instructorId: string | null) => void;
  onCancelReasonChange?: (reason: string) => void;
  instructorInfo?: Instructor;
  isOwner?: boolean;
}) {
  const [expandedLineItems, setExpandedLineItems] = useState(false);
  const [editTarget, setEditTarget] = useState<LineItemEditTarget | null>(null);

  const handleAdjustClick = (lineItem: SettlementLineItem) => {
    setEditTarget({
      instructorId: item.instructorId,
      classId: lineItem.classId,
      currentAmount: lineItem.amount,
    });
  };

  const handleAdjustSuccess = () => {
    setEditTarget(null);
    onAdjustSuccess();
  };

  const handleAdjustCancel = () => {
    setEditTarget(null);
  };

  return (
    <>
      <tr>
        <InstructorNameCell
          instructorId={item.instructorId}
          name={instructorInfo?.name}
          centerId={centerId}
          seniorSince={instructorInfo?.seniorSince}
          isOwner={isOwner}
        />
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {item.completedClasses}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div className="flex items-center gap-2">
            <span>{formatAmount(item.totalAmount)}</span>
            {item.lineItems.length > 0 && (
              <button
                onClick={() => setExpandedLineItems((prev) => !prev)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
                aria-expanded={expandedLineItems}
              >
                {expandedLineItems ? "접기" : `상세 (${item.lineItems.length}건)`}
              </button>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {formatAmount(calculateAfterTax(item.totalAmount))}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            정산 완료
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          {onCancel && cancelReasonTarget !== item.instructorId && (
            <button
              onClick={() => onCancelReasonTargetChange?.(item.instructorId)}
              disabled={isCancelling}
              className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              정산 취소
            </button>
          )}
          {cancelReasonTarget === item.instructorId && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={cancelReason ?? ""}
                onChange={(e) => onCancelReasonChange?.(e.target.value)}
                className="w-40 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500"
                placeholder="취소 사유"
                aria-label="취소 사유"
              />
              <button
                onClick={() => {
                  if (!cancelReason?.trim()) return;
                  if (window.confirm("정산을 취소하시겠습니까?")) {
                    onCancel?.(item.instructorId, cancelReason.trim());
                  }
                }}
                disabled={isCancelling || !cancelReason?.trim()}
                className="px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isCancelling ? "취소 중..." : "취소 확정"}
              </button>
              <button
                onClick={() => onCancelReasonTargetChange?.(null)}
                disabled={isCancelling}
                className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                닫기
              </button>
            </div>
          )}
        </td>
      </tr>
      {/* 라인 아이템 상세 행 */}
      {expandedLineItems && (
        <tr>
          <td colSpan={6} className="px-6 py-2 bg-gray-50">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">수업별 정산 내역</p>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100 text-gray-500">
                    <th className="px-2 py-1.5 text-left font-medium">수업일</th>
                    <th className="px-2 py-1.5 text-left font-medium">회원명</th>
                    <th className="px-2 py-1.5 text-left font-medium">회원 유형</th>
                    <th className="px-2 py-1.5 text-left font-medium">수업 형태</th>
                    <th className="px-2 py-1.5 text-right font-medium">금액 (VAT 포함)</th>
                    <th className="px-2 py-1.5 text-right font-medium">세후</th>
                    {isCalculating && <th className="px-2 py-1.5 text-left font-medium">조정</th>}
                  </tr>
                </thead>
                <tbody>
                  {item.lineItems.map((lineItem) => (
                    <React.Fragment key={lineItem.classId}>
                      <tr className="border-t border-gray-100 text-gray-700">
                        {/* 수업일 컬럼 */}
                        <td className="px-2 py-1.5">{lineItem.classDate ?? "-"}</td>

                        {/* 회원명 컬럼: memberNames 배열 또는 단수 memberName 지원 */}
                        <td className="px-2 py-1.5">
                          {lineItem.memberNames && lineItem.memberNames.length > 0 ? (
                            lineItem.memberNames.length > 1 ? (
                              <span>
                                {lineItem.memberNames[0]} 외 {lineItem.memberNames.length - 1}명
                              </span>
                            ) : lineItem.studiomateMemberIds?.[0] ? (
                              <Link
                                to={`/centers/${centerId}/members/${lineItem.studiomateMemberIds[0]}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {lineItem.memberNames[0]}
                              </Link>
                            ) : (
                              <span>{lineItem.memberNames[0]}</span>
                            )
                          ) : lineItem.memberName ? (
                            lineItem.studiomateMemberId ? (
                              <Link
                                to={`/centers/${centerId}/members/${lineItem.studiomateMemberId}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {lineItem.memberName}
                              </Link>
                            ) : (
                              <span>{lineItem.memberName}</span>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* 회원 유형 컬럼 */}
                        <td className="px-2 py-1.5">
                          {lineItem.memberType === null ? (
                            <span className="text-gray-400">N/A</span>
                          ) : lineItem.memberType === "firstPurchase" ? (
                            "첫 구매"
                          ) : (
                            "재등록"
                          )}
                        </td>

                        {/* 수업 형태 컬럼 — @ClassType-values */}
                        <td className="px-2 py-1.5">
                          {(() => {
                            const ct = normalizeClassType(lineItem.classType);
                            if (ct === "TRIAL_COUNSEL") return "체험단";
                            const isGroup = ct === "GROUP_REGULAR";
                            const memberCount =
                              lineItem.memberIds?.length ?? lineItem.memberNames?.length ?? 1;
                            if (lineItem.isOt) return isGroup ? "그룹 OT" : "1:1 OT";
                            if (isGroup && memberCount > 1) return `그룹 (${memberCount}명)`;
                            return isGroup ? "그룹" : "1:1";
                          })()}
                        </td>

                        {/* 금액 컬럼 */}
                        <td className="px-2 py-1.5 text-right font-medium">
                          {formatAmount(lineItem.amount)}
                        </td>

                        {/* 세후 금액 컬럼 */}
                        <td className="px-2 py-1.5 text-right text-gray-500">
                          {formatAmount(calculateAfterTax(lineItem.amount))}
                        </td>

                        {/* 조정 버튼 */}
                        {isCalculating && (
                          <td className="px-2 py-1.5">
                            {editTarget?.classId !== lineItem.classId && (
                              <button
                                onClick={() => handleAdjustClick(lineItem)}
                                className="text-xs text-orange-600 hover:text-orange-800 underline"
                              >
                                조정
                              </button>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* 라인 아이템 조정 폼 */}
                      {isCalculating && editTarget?.classId === lineItem.classId && (
                        <tr>
                          <td colSpan={isCalculating ? 7 : 6} className="px-2 pb-2">
                            <LineItemAdjustForm
                              centerId={centerId}
                              yearMonth={yearMonth}
                              target={editTarget}
                              onSuccess={handleAdjustSuccess}
                              onCancel={handleAdjustCancel}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// --- 메인 컴포넌트 ---

export function AdminSettlementPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();

  // Bug 2 fix: yearMonth는 searchParams에서 직접 파생 (로컬 상태 복사 없음)
  // → 새로고침 시에도 URL의 month 파라미터가 그대로 반영된다.
  const [searchParams, setSearchParams] = useSearchParams();
  const yearMonth = searchParams.get("month") ?? getCurrentYearMonth();

  const handleSetYearMonth = useCallback(
    (month: string) => {
      setSearchParams({ month }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    trackEvent(EVENT_NAMES.SETTLEMENT_VIEWED, { year_month: yearMonth });
  }, [yearMonth]);

  // Bug 1 fix: 매핑된 강사 목록을 Studiomate에서 로드
  const { data: staffsData, isLoading: instructorsLoading } = useStudiomateStaffs(Number(centerId));
  const mappedInstructors: InstructorMappingState[] =
    staffsData?.instructors?.filter((i: InstructorMappingState) => i.mapping !== null) ?? [];

  // 강사 시니어 정보 로드
  const [instructorMap, setInstructorMap] = useState<Map<string, Instructor>>(new Map());
  useEffect(() => {
    if (!centerId) return;
    void getInstructors(user.uid, Number(centerId))
      .then((list) => {
        setInstructorMap(new Map(list.map((i) => [i.id, i])));
      })
      .catch(() => {
        // 강사 정보 로딩 실패 시 빈 맵 유지 (시니어 배지 미표시)
      });
  }, [user.uid, centerId]);

  const [settlement, setSettlement] = useState<AdminSettlementDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  // fetchKey를 증가시켜 동일 연월에서 정산 데이터 재조회를 트리거한다.
  const [fetchKey, setFetchKey] = useState(0);

  // 강사별 정산 처리 상태
  const [settlingInstructors, setSettlingInstructors] = useState<Set<string>>(new Set());
  const [instructorSettleErrors, setInstructorSettleErrors] = useState<Map<string, string>>(
    new Map(),
  );

  // 강사별 미리보기 상태
  const [previewMap, setPreviewMap] = useState<
    Map<string, { lineItems: SettlementLineItem[]; completedClasses: number; totalAmount: number }>
  >(new Map());
  const [previewingInstructors, setPreviewingInstructors] = useState<Set<string>>(new Set());
  // 강사별 정산 취소 상태
  const [cancellingInstructors, setCancellingInstructors] = useState<Set<string>>(new Set());
  const [cancelReasonTarget, setCancelReasonTarget] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // 정산 기간 경고 모달 상태
  const [warningModal, setWarningModal] = useState<{
    isOpen: boolean;
    action: (() => void) | null;
  }>({ isOpen: false, action: null });

  /** 이전 달이 아닌 기간에 정산 액션 실행 시 경고 모달을 표시한다. */
  const guardSettlementAction = useCallback(
    (action: () => void) => {
      if (yearMonth !== getPreviousYearMonth()) {
        setWarningModal({ isOpen: true, action });
      } else {
        action();
      }
    },
    [yearMonth],
  );

  // 정산 상태 변경 핸들러: Cloud Function updateSettlementStatus를 호출한다.
  const handleUpdateStatus = useCallback(
    async (newStatus: "confirmed" | "paid") => {
      if (!centerId) return;
      setIsUpdatingStatus(true);
      setStatusUpdateError(null);
      try {
        const callable = httpsCallable(getFirebaseFunctions(), "updateSettlementStatus");
        await callable({ centerId: Number(centerId), yearMonth, newStatus });
        // 성공 후 fetchKey를 증가시켜 정산 데이터 재조회를 트리거한다.
        setFetchKey((prev) => prev + 1);
      } catch (err: unknown) {
        setStatusUpdateError(err instanceof Error ? err.message : "정산 상태 변경에 실패했습니다.");
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [centerId, yearMonth],
  );

  /** 라인 아이템 조정 성공 시 정산 데이터를 재조회하는 핸들러 */
  const handleAdjustSuccess = useCallback(() => {
    setFetchKey((prev) => prev + 1);
  }, []);

  /** 강사별 미리보기: previewInstructorSettlement CF를 호출한다. */
  const handlePreviewInstructor = useCallback(
    async (instructorId: string) => {
      if (!centerId) return;
      setPreviewingInstructors((prev) => new Set(prev).add(instructorId));
      setInstructorSettleErrors((prev) => {
        const m = new Map(prev);
        m.delete(instructorId);
        return m;
      });
      try {
        const result = await previewInstructorSettlementAction(
          Number(centerId),
          instructorId,
          yearMonth,
        );
        setPreviewMap((prev) =>
          new Map(prev).set(instructorId, {
            lineItems: result.instructorSettlement.lineItems,
            completedClasses: result.instructorSettlement.completedClasses,
            totalAmount: result.instructorSettlement.totalAmount,
          }),
        );
      } catch (err: unknown) {
        setInstructorSettleErrors((prev) => {
          const m = new Map(prev);
          m.set(instructorId, err instanceof Error ? err.message : "미리보기에 실패했습니다.");
          return m;
        });
      } finally {
        setPreviewingInstructors((prev) => {
          const s = new Set(prev);
          s.delete(instructorId);
          return s;
        });
      }
    },
    [centerId, yearMonth],
  );

  /** 강사별 정산 처리: settleInstructor CF를 호출하여 해당 강사의 정산을 실행한다. */
  const handleSettleInstructor = useCallback(
    async (instructorId: string) => {
      if (!centerId) return;
      setSettlingInstructors((prev) => new Set(prev).add(instructorId));
      setInstructorSettleErrors((prev) => {
        const m = new Map(prev);
        m.delete(instructorId);
        return m;
      });
      try {
        await settleInstructorAction(Number(centerId), instructorId, yearMonth);
        // 정산 완료 후 미리보기 상태 정리
        setPreviewMap((prev) => {
          const m = new Map(prev);
          m.delete(instructorId);
          return m;
        });
        setFetchKey((prev) => prev + 1);
      } catch (err: unknown) {
        setInstructorSettleErrors((prev) => {
          const m = new Map(prev);
          m.set(instructorId, err instanceof Error ? err.message : "정산 처리에 실패했습니다.");
          return m;
        });
      } finally {
        setSettlingInstructors((prev) => {
          const s = new Set(prev);
          s.delete(instructorId);
          return s;
        });
      }
    },
    [centerId, yearMonth],
  );

  /** 강사별 정산 취소: cancelInstructorSettlement CF를 호출하여 해당 강사의 정산을 취소한다. */
  const handleCancelInstructor = useCallback(
    async (instructorId: string, reason: string) => {
      if (!centerId) return;
      setCancellingInstructors((prev) => new Set(prev).add(instructorId));
      setInstructorSettleErrors((prev) => {
        const m = new Map(prev);
        m.delete(instructorId);
        return m;
      });
      try {
        await cancelInstructorSettlementAction(Number(centerId), instructorId, yearMonth, reason);
        setCancelReasonTarget(null);
        setCancelReason("");
        setFetchKey((prev) => prev + 1);
      } catch (err: unknown) {
        setInstructorSettleErrors((prev) => {
          const m = new Map(prev);
          m.set(instructorId, err instanceof Error ? err.message : "정산 취소에 실패했습니다.");
          return m;
        });
      } finally {
        setCancellingInstructors((prev) => {
          const s = new Set(prev);
          s.delete(instructorId);
          return s;
        });
      }
    },
    [centerId, yearMonth],
  );

  // 연월 변경 또는 초기 로드 시 정산 데이터를 Firestore에서 조회한다.
  useEffect(() => {
    let cancelled = false;

    // 월 변경 시 미리보기 상태 초기화
    setPreviewMap(new Map());

    async function fetchSettlement() {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getAdminSettlement(user.uid, Number(centerId), yearMonth);
        if (!cancelled) {
          setSettlement(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("정산 데이터 조회 실패"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchSettlement();

    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId, yearMonth, fetchKey]);

  // 정산 데이터를 instructorId → InstructorSettlement 맵으로 변환
  const settledMap = new Map<string, InstructorSettlement>(
    settlement?.instructorSettlements?.map((s) => [s.instructorId, s]) ?? [],
  );

  // 매핑된 강사 목록에 없는 정산 엔트리 (이미 정산되었으나 매핑 해제된 강사)
  const settledNotMapped = (settlement?.instructorSettlements ?? []).filter(
    (s) => !mappedInstructors.some((i) => i.instructorId === s.instructorId),
  );

  // --- 로딩 상태 ---
  if (isLoading || instructorsLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center">로딩 중...</p>
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  // --- 정상 상태 ---
  const badge = settlement ? getStatusBadge(settlement.status) : null;
  const isCalculating = settlement?.status === "calculating";

  return (
    <div className="space-y-6">
      {/* 뒤로가기 링크 */}
      <div>
        <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 상세
        </Link>
      </div>

      {/* 헤더: 정산 관리 제목 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">정산 관리</h1>

        {/* 월 네비게이션 */}
        <div className="mt-4 flex items-center gap-4">
          <button
            aria-label="이전 달"
            onClick={() => handleSetYearMonth(getPrevMonth(yearMonth))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            &larr;
          </button>

          <span className="text-lg font-semibold text-gray-900 min-w-[6rem] text-center">
            {yearMonth}
          </span>

          <button
            aria-label="다음 달"
            onClick={() => handleSetYearMonth(getNextMonth(yearMonth))}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            &rarr;
          </button>

          {/* 정산 상태 배지 */}
          {badge && (
            <span
              className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}
            >
              {badge.label}
            </span>
          )}

          {/* 정산 상태 변경 액션 버튼: calculating → confirmed, confirmed → paid */}
          {settlement?.status === "calculating" && (
            <button
              onClick={() => guardSettlementAction(() => void handleUpdateStatus("confirmed"))}
              disabled={isUpdatingStatus}
              className="ml-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              확정
            </button>
          )}
          {settlement?.status === "confirmed" && (
            <button
              onClick={() => void handleUpdateStatus("paid")}
              disabled={isUpdatingStatus}
              className="ml-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              지급 완료
            </button>
          )}

          {/* CSV 내보내기 버튼: 정산 데이터가 있을 때만 표시 */}
          {settlement && (
            <button
              aria-label="CSV 내보내기"
              onClick={() => exportSettlementToCsv(settlement, yearMonth, instructorMap)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              CSV 내보내기
            </button>
          )}
        </div>

        {/* 정산 상태 변경 오류 메시지 */}
        {statusUpdateError && <p className="mt-2 text-sm text-red-600">{statusUpdateError}</p>}

        {/* 라인 아이템 조정 안내: calculating 상태에서만 표시 */}
        {isCalculating && (
          <p className="mt-2 text-xs text-gray-500">
            산출 중 상태에서는 각 수업의 금액을 수동으로 조정할 수 있습니다. 강사별 상세를 펼쳐
            &quot;조정&quot; 버튼을 클릭하세요.
          </p>
        )}
      </div>

      {/* 정산 기간 경고 모달 */}
      <SettlementWarningModal
        isOpen={warningModal.isOpen}
        yearMonth={yearMonth}
        onConfirm={() => {
          warningModal.action?.();
          setWarningModal({ isOpen: false, action: null });
        }}
        onCancel={() => setWarningModal({ isOpen: false, action: null })}
      />

      {/* 강사별 정산 테이블 */}
      <div className="bg-white rounded-lg shadow">
        {mappedInstructors.length === 0 && settledNotMapped.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">매핑된 강사가 없습니다.</p>
            <p className="text-sm text-gray-400 mt-1">강사 매핑 후 정산을 진행할 수 있습니다.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    강사
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    완료 수업 수
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    정산 금액 (VAT 포함)
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    실지급액 (세후)
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    상태
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* 매핑된 강사 목록 */}
                {mappedInstructors.map((instructor) => {
                  const settled = settledMap.get(instructor.instructorId);
                  const isSettling = settlingInstructors.has(instructor.instructorId);
                  const settleError = instructorSettleErrors.get(instructor.instructorId);

                  if (settled) {
                    return (
                      <React.Fragment key={instructor.instructorId}>
                        <InstructorSettlementRow
                          item={settled}
                          centerId={Number(centerId)}
                          yearMonth={yearMonth}
                          isCalculating={isCalculating ?? false}
                          onAdjustSuccess={handleAdjustSuccess}
                          onCancel={handleCancelInstructor}
                          isCancelling={cancellingInstructors.has(instructor.instructorId)}
                          cancelReasonTarget={cancelReasonTarget}
                          cancelReason={cancelReason}
                          onCancelReasonTargetChange={(id) => {
                            setCancelReasonTarget(id);
                            setCancelReason("");
                          }}
                          onCancelReasonChange={setCancelReason}
                          instructorInfo={instructorMap.get(instructor.instructorId)}
                          isOwner={instructor.isOwner}
                        />
                        {instructorSettleErrors.get(instructor.instructorId) && (
                          <tr>
                            <td colSpan={6} className="px-6 py-2 text-xs text-red-600 bg-red-50">
                              {instructorSettleErrors.get(instructor.instructorId)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  // 미정산 강사: "미리보기" → "정산 처리" 플로우
                  const preview = previewMap.get(instructor.instructorId);
                  const isPreviewing = previewingInstructors.has(instructor.instructorId);

                  return (
                    <React.Fragment key={instructor.instructorId}>
                      <tr>
                        <InstructorNameCell
                          instructorId={instructor.instructorId}
                          name={
                            instructorMap.get(instructor.instructorId)?.name ??
                            instructor.instructorName
                          }
                          centerId={Number(centerId)}
                          seniorSince={instructorMap.get(instructor.instructorId)?.seniorSince}
                          isOwner={instructor.isOwner}
                        />
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {preview ? preview.completedClasses : "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {preview ? formatAmount(preview.totalAmount) : "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {preview ? formatAmount(calculateAfterTax(preview.totalAmount)) : "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                            미정산
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            {!preview && (
                              <button
                                onClick={() =>
                                  guardSettlementAction(
                                    () => void handlePreviewInstructor(instructor.instructorId),
                                  )
                                }
                                disabled={
                                  isPreviewing ||
                                  settlement?.status === "confirmed" ||
                                  settlement?.status === "paid"
                                }
                                className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {isPreviewing ? "로딩..." : "미리보기"}
                              </button>
                            )}
                            <button
                              onClick={() =>
                                guardSettlementAction(
                                  () => void handleSettleInstructor(instructor.instructorId),
                                )
                              }
                              disabled={
                                isSettling ||
                                settlement?.status === "confirmed" ||
                                settlement?.status === "paid"
                              }
                              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSettling ? "처리 중..." : "정산 처리"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* 미리보기 라인 아이템 상세 */}
                      {preview && (
                        <tr>
                          <td colSpan={6} className="px-6 py-2 bg-blue-50">
                            <div>
                              <p className="text-xs font-medium text-blue-700 mb-2">
                                미리보기 — 수업별 정산 내역
                              </p>
                              <table className="w-full text-xs border-collapse">
                                <thead>
                                  <tr className="bg-blue-100 text-gray-600">
                                    <th className="px-2 py-1.5 text-left font-medium">수업일</th>
                                    <th className="px-2 py-1.5 text-left font-medium">회원명</th>
                                    <th className="px-2 py-1.5 text-left font-medium">회원 유형</th>
                                    <th className="px-2 py-1.5 text-left font-medium">수업 형태</th>
                                    <th className="px-2 py-1.5 text-right font-medium">금액</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {preview.lineItems.map((lineItem) => {
                                    const ct = normalizeClassType(lineItem.classType);
                                    const isTrialCounsel = ct === "TRIAL_COUNSEL";
                                    const isGroup = ct === "GROUP_REGULAR" && !lineItem.isOt;
                                    const isGroupOt = ct === "GROUP_REGULAR" && lineItem.isOt;
                                    const memberCount =
                                      lineItem.memberIds?.length ??
                                      lineItem.memberNames?.length ??
                                      1;

                                    return (
                                      <tr
                                        key={lineItem.classId}
                                        className="border-t border-blue-100 text-gray-700"
                                      >
                                        <td className="px-2 py-1.5">{lineItem.classDate ?? "-"}</td>
                                        <td className="px-2 py-1.5">
                                          {isTrialCounsel ? (
                                            <span className="text-gray-400">
                                              {lineItem.notes ?? "-"}
                                            </span>
                                          ) : lineItem.memberNames &&
                                            lineItem.memberNames.length > 1 ? (
                                            `${lineItem.memberNames[0]} 외 ${lineItem.memberNames.length - 1}명`
                                          ) : (
                                            (lineItem.memberNames?.[0] ??
                                            lineItem.memberName ??
                                            "-")
                                          )}
                                        </td>
                                        <td className="px-2 py-1.5">
                                          {lineItem.memberType === null ? (
                                            <span className="text-gray-400">N/A</span>
                                          ) : lineItem.memberType === "firstPurchase" ? (
                                            "첫 구매"
                                          ) : (
                                            "재등록"
                                          )}
                                        </td>
                                        <td className="px-2 py-1.5">
                                          {isTrialCounsel
                                            ? "체험단"
                                            : isGroupOt
                                              ? "그룹 OT"
                                              : isGroup
                                                ? `그룹 (${memberCount}명)`
                                                : lineItem.isOt
                                                  ? "1:1 OT"
                                                  : "1:1"}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-medium">
                                          {formatAmount(lineItem.amount)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                              <div className="mt-2 flex items-center justify-between">
                                <button
                                  onClick={() => {
                                    setPreviewMap((prev) => {
                                      const m = new Map(prev);
                                      m.delete(instructor.instructorId);
                                      return m;
                                    });
                                  }}
                                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                                >
                                  미리보기 닫기
                                </button>
                                <span className="text-xs text-gray-500">
                                  합계: {formatAmount(preview.totalAmount)}
                                  {isPreviewing && " (갱신 중...)"}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {settleError && (
                        <tr>
                          <td colSpan={6} className="px-6 py-2 text-xs text-red-600 bg-red-50">
                            {settleError}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* 매핑 해제된 강사의 기존 정산 데이터 */}
                {settledNotMapped.map((item) => (
                  <InstructorSettlementRow
                    key={item.instructorId}
                    item={item}
                    centerId={Number(centerId)}
                    yearMonth={yearMonth}
                    isCalculating={isCalculating ?? false}
                    onAdjustSuccess={handleAdjustSuccess}
                    onCancel={handleCancelInstructor}
                    isCancelling={cancellingInstructors.has(item.instructorId)}
                    cancelReasonTarget={cancelReasonTarget}
                    cancelReason={cancelReason}
                    onCancelReasonTargetChange={(id) => {
                      setCancelReasonTarget(id);
                      setCancelReason("");
                    }}
                    onCancelReasonChange={setCancelReason}
                    instructorInfo={instructorMap.get(item.instructorId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
